// ─────────────────────────────────────────────────────────────────
// Glass-i — Purchase Bill Accounting Posting helpers (Stage 6P.6).
//
// Pure ESM module — exercised by the frontend node:test runner.
//
// Frontend mirror of backend Purchase Bill → Accounting posting
// math + payload shaping. The backend
// (`backend/src/services/erp/purchaseAccounting/purchaseAccounting.service.js`
// — plus the shared accounting `voucherPosting.service.js`)
// remains the source of truth for:
//   - posting_no allocation (PAP-YYYYMMDD-NNNNNN)
//   - per-item-type purchase ledger code resolution
//     (GLASS → PUR-RAWGLASS → fallback PURCHASE, etc.)
//   - intra-vs-inter-state CGST/SGST vs IGST split
//   - duplicate-block decisions (one posting per purchase_bill_id)
//   - round-off line generation
//   - ITC register row emission
//   - supplier-payable line totals
//
// These helpers exist only so the Stage 6P.6 preview / post UI can
// show consistent debit/credit totals, status badges and capability
// gates while the operator reviews the posting preview. The API
// recomputes on post and is the final authority.
//
// Mirrors the style of:
//   - frontend/src/pages/erp/inventory/inventoryHelpers.js
//     (option lists, normalise/build, capability gates, status colour
//     palette, tolerant numeric coercion, frozen enum mirrors).
//   - backend/src/services/erp/purchaseAccounting/purchaseAccounting.constants.js
//     (enum lists — frontend mirrors are sealed with Object.freeze so
//     callers can't mutate a shared array by accident).
// ─────────────────────────────────────────────────────────────────


// ─────────────────────────────────────────────────────────────────
// Enum mirrors — kept aligned with backend
// services/erp/purchaseAccounting/purchaseAccounting.constants.js.
// Frozen so callers can't mutate the shared lists by accident.
// ─────────────────────────────────────────────────────────────────

export const POSTING_STATUSES = Object.freeze([
  'NOT_POSTED',
  'POSTING_READY',
  'POSTED',
  'POSTING_FAILED',
  'CANCELLED'
]);

export const POSTING_LINE_TYPES = Object.freeze([
  'PURCHASE',
  'INPUT_CGST',
  'INPUT_SGST',
  'INPUT_IGST',
  'SUPPLIER_PAYABLE',
  'ROUND_OFF_GAIN',
  'ROUND_OFF_LOSS'
]);

export const ITC_STATUSES = Object.freeze([
  'ELIGIBLE',
  'BLOCKED',
  'REVERSED',
  'CLAIMED_LATER'
]);

export const EVENT_TYPES = Object.freeze([
  'CREATED',
  'POSTING_READY',
  'POSTED',
  'POSTING_FAILED',
  'DUPLICATE_BLOCKED',
  'CANCELLED'
]);


// ─────────────────────────────────────────────────────────────────
// Private constants used by the math + classifier helpers.
// ─────────────────────────────────────────────────────────────────

const POSTING_STATUSES_SET    = new Set(POSTING_STATUSES);
const POSTING_LINE_TYPES_SET  = new Set(POSTING_LINE_TYPES);
const ITC_STATUSES_SET        = new Set(ITC_STATUSES);
const EVENT_TYPES_SET         = new Set(EVENT_TYPES);

// Bill-side guardrails — the parent purchase bill must be approved /
// matched before its accounting voucher can be generated. We keep
// these as private string constants so a typo at a call site can't
// silently re-enable the CTA.
const BILL_STATUS_APPROVED = 'APPROVED';
const BILL_STATUS_POSTED   = 'POSTED';
const BILL_STATUS_MATCHED  = 'MATCHED';

const BILL_MATCH_OK            = 'MATCHED';
const BILL_MATCH_NOT_REQUIRED  = 'NOT_REQUIRED';
const BILL_MATCH_PENDING       = 'PENDING';
const BILL_MATCH_REJECTED      = 'REJECTED';


// ─────────────────────────────────────────────────────────────────
// Tolerant numeric coercion — mirrors backend toNumber.
// Null / undefined / empty string / non-finite all collapse to 0.
// Never throws, never returns NaN.
// ─────────────────────────────────────────────────────────────────

function toNumber(value) {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return 0;
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : 0;
  }
  if (typeof value === 'boolean') return value ? 1 : 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

// Half-up rounding — defeats IEEE-754 surprises like 1.005 → 1.0049…
function roundHalfUp(value, decimals) {
  const n = toNumber(value);
  if (n === 0) return 0;
  const factor = Math.pow(10, decimals);
  const sign   = n < 0 ? -1 : 1;
  const abs    = Math.abs(n);
  const scaled = abs * factor + 1e-9;
  return (sign * Math.round(scaled)) / factor;
}

function round2(n) { return roundHalfUp(n, 2); }


// ─────────────────────────────────────────────────────────────────
// Snake / camel access — preview rows arrive from the API in camel,
// but ledger / posting rows arrive in both depending on whether they
// were freshly mapped or replayed from a server cache.
// We probe both and never mutate the caller.
// ─────────────────────────────────────────────────────────────────

function pick(obj, snake, camel, fallback) {
  if (!obj || typeof obj !== 'object') return fallback;
  if (snake in obj && obj[snake] !== undefined) return obj[snake];
  if (camel in obj && obj[camel] !== undefined) return obj[camel];
  return fallback;
}

function pickStringUpper(obj, snake, camel) {
  const v = pick(obj, snake, camel, '');
  if (v === null || v === undefined) return '';
  return String(v).trim().toUpperCase();
}


// ─────────────────────────────────────────────────────────────────
// Row normalisers — the API returns camelCase rows already, but the
// grid + drawer also consume rows replayed from local state or
// fixture data that may carry either casing. These normalisers return
// a canonical camelCase shape with widened numbers, never mutating
// the input.
//
// Anything we don't recognise is preserved on the way out so the
// grid can show columns we don't know about (e.g. future tenant
// extensions) without losing data.
// ─────────────────────────────────────────────────────────────────

export function normalizePurchaseAccountingPosting(row) {
  if (!row || typeof row !== 'object') return null;
  const out = Object.assign({}, row);

  out.id                  = pick(row, 'id', 'id', null);
  out.postingNo           = pick(row, 'posting_no',         'postingNo',         null);
  out.postingDate         = pick(row, 'posting_date',       'postingDate',       null);
  out.postingStatus       = pickStringUpper(row, 'posting_status', 'postingStatus') || null;

  out.purchaseBillId      = pick(row, 'purchase_bill_id',   'purchaseBillId',    null);
  out.purchaseBillNo      = pick(row, 'purchase_bill_no',   'purchaseBillNo',    null);
  out.purchaseBillDate    = pick(row, 'purchase_bill_date', 'purchaseBillDate',  null);

  out.supplierId          = pick(row, 'supplier_id',        'supplierId',        null);
  out.supplierName        = pick(row, 'supplier_name',      'supplierName',      null);
  out.supplierGstin       = pick(row, 'supplier_gstin',     'supplierGstin',     null);
  out.supplierStateCode   = pick(row, 'supplier_state_code','supplierStateCode', null);
  out.placeOfSupplyCode   = pick(row, 'place_of_supply_code','placeOfSupplyCode', null);
  out.isInterState        = Boolean(pick(row, 'is_inter_state', 'isInterState', false));

  out.subTotal            = toNumber(pick(row, 'sub_total',          'subTotal',          0));
  out.discountAmount      = toNumber(pick(row, 'discount_amount',    'discountAmount',    0));
  out.taxableAmount       = toNumber(pick(row, 'taxable_amount',     'taxableAmount',     0));
  out.cgstAmount          = toNumber(pick(row, 'cgst_amount',        'cgstAmount',        0));
  out.sgstAmount          = toNumber(pick(row, 'sgst_amount',        'sgstAmount',        0));
  out.igstAmount          = toNumber(pick(row, 'igst_amount',        'igstAmount',        0));
  out.totalTaxAmount      = toNumber(pick(row, 'total_tax_amount',   'totalTaxAmount',    0));
  out.roundOffAmount      = toNumber(pick(row, 'round_off_amount',   'roundOffAmount',    0));
  out.grandTotal          = toNumber(pick(row, 'grand_total',        'grandTotal',        0));

  out.voucherId           = pick(row, 'voucher_id',     'voucherId',     null);
  out.voucherNo           = pick(row, 'voucher_no',     'voucherNo',     null);
  out.voucherDate         = pick(row, 'voucher_date',   'voucherDate',   null);

  out.itcStatus           = pickStringUpper(row, 'itc_status', 'itcStatus') || null;
  out.itcEligibleAmount   = toNumber(pick(row, 'itc_eligible_amount', 'itcEligibleAmount', 0));
  out.itcBlockedAmount    = toNumber(pick(row, 'itc_blocked_amount',  'itcBlockedAmount',  0));

  out.postedBy            = pick(row, 'posted_by',         'postedBy',         null);
  out.postedAt            = pick(row, 'posted_at',         'postedAt',         null);
  out.cancelledBy         = pick(row, 'cancelled_by',      'cancelledBy',      null);
  out.cancelledAt         = pick(row, 'cancelled_at',      'cancelledAt',      null);
  out.cancellationReason  = pick(row, 'cancellation_reason','cancellationReason', null);
  out.failureReason       = pick(row, 'failure_reason',    'failureReason',    null);
  out.remarks             = pick(row, 'remarks', 'remarks', null);

  out.createdAt           = pick(row, 'created_at', 'createdAt', null);
  out.updatedAt           = pick(row, 'updated_at', 'updatedAt', null);

  return out;
}

export function normalizeAccountingPreviewLine(line) {
  if (!line || typeof line !== 'object') return null;
  const out = Object.assign({}, line);

  out.lineType        = pickStringUpper(line, 'line_type', 'lineType') || null;
  out.lineNo          = toNumber(pick(line, 'line_no', 'lineNo', 0)) || 0;

  out.ledgerAccountId   = pick(line, 'ledger_account_id',   'ledgerAccountId',   null);
  out.ledgerAccountCode = pick(line, 'ledger_account_code', 'ledgerAccountCode', null);
  out.ledgerAccountName = pick(line, 'ledger_account_name', 'ledgerAccountName', null);

  out.itemType        = pickStringUpper(line, 'item_type', 'itemType') || null;
  out.purchaseBillLineId = pick(line, 'purchase_bill_line_id', 'purchaseBillLineId', null);

  out.debitAmount     = toNumber(pick(line, 'debit_amount',  'debitAmount',  0));
  out.creditAmount    = toNumber(pick(line, 'credit_amount', 'creditAmount', 0));

  out.taxableBase     = toNumber(pick(line, 'taxable_base',  'taxableBase',  0));
  out.taxRatePercent  = toNumber(pick(line, 'tax_rate_percent', 'taxRatePercent', 0));

  out.narration       = pick(line, 'narration', 'narration', null);

  return out;
}

export function normalizeGstItcRow(row) {
  if (!row || typeof row !== 'object') return null;
  const out = Object.assign({}, row);

  out.id                = pick(row, 'id', 'id', null);
  out.postingId         = pick(row, 'posting_id',       'postingId',       null);
  out.postingNo         = pick(row, 'posting_no',       'postingNo',       null);
  out.purchaseBillId    = pick(row, 'purchase_bill_id', 'purchaseBillId',  null);
  out.purchaseBillNo    = pick(row, 'purchase_bill_no', 'purchaseBillNo',  null);
  out.purchaseBillDate  = pick(row, 'purchase_bill_date', 'purchaseBillDate', null);

  out.supplierId        = pick(row, 'supplier_id',        'supplierId',        null);
  out.supplierName      = pick(row, 'supplier_name',      'supplierName',      null);
  out.supplierGstin     = pick(row, 'supplier_gstin',     'supplierGstin',     null);
  out.placeOfSupplyCode = pick(row, 'place_of_supply_code','placeOfSupplyCode', null);
  out.isInterState      = Boolean(pick(row, 'is_inter_state', 'isInterState', false));

  out.taxableAmount     = toNumber(pick(row, 'taxable_amount', 'taxableAmount', 0));
  out.cgstAmount        = toNumber(pick(row, 'cgst_amount',    'cgstAmount',    0));
  out.sgstAmount        = toNumber(pick(row, 'sgst_amount',    'sgstAmount',    0));
  out.igstAmount        = toNumber(pick(row, 'igst_amount',    'igstAmount',    0));
  out.totalTaxAmount    = toNumber(pick(row, 'total_tax_amount', 'totalTaxAmount', 0));

  out.itcStatus         = pickStringUpper(row, 'itc_status', 'itcStatus') || null;
  out.itcEligibleAmount = toNumber(pick(row, 'itc_eligible_amount', 'itcEligibleAmount', 0));
  out.itcBlockedAmount  = toNumber(pick(row, 'itc_blocked_amount',  'itcBlockedAmount',  0));
  out.itcClaimedPeriod  = pick(row, 'itc_claimed_period', 'itcClaimedPeriod', null);
  out.reversalReason    = pick(row, 'reversal_reason',    'reversalReason',   null);

  return out;
}

export function normalizeSupplierPayableRow(row) {
  if (!row || typeof row !== 'object') return null;
  const out = Object.assign({}, row);

  out.supplierId     = pick(row, 'supplier_id',   'supplierId',   null);
  out.supplierName   = pick(row, 'supplier_name', 'supplierName', null);
  out.supplierCode   = pick(row, 'supplier_code', 'supplierCode', null);
  out.supplierGstin  = pick(row, 'supplier_gstin','supplierGstin',null);

  out.postingId      = pick(row, 'posting_id',         'postingId',         null);
  out.postingNo      = pick(row, 'posting_no',         'postingNo',         null);
  out.postingDate    = pick(row, 'posting_date',       'postingDate',       null);
  out.purchaseBillId = pick(row, 'purchase_bill_id',   'purchaseBillId',    null);
  out.purchaseBillNo = pick(row, 'purchase_bill_no',   'purchaseBillNo',    null);

  out.payableAmount  = toNumber(pick(row, 'payable_amount',  'payableAmount',  0));
  out.paidAmount     = toNumber(pick(row, 'paid_amount',     'paidAmount',     0));
  out.outstandingAmount = toNumber(
    pick(row, 'outstanding_amount', 'outstandingAmount',
      // Derive when neither side sent a value.
      toNumber(pick(row, 'payable_amount', 'payableAmount', 0)) -
      toNumber(pick(row, 'paid_amount',    'paidAmount',    0))
    )
  );

  out.dueDate        = pick(row, 'due_date',     'dueDate',     null);
  out.ageDays        = toNumber(pick(row, 'age_days', 'ageDays', 0)) || 0;
  out.currency       = pick(row, 'currency', 'currency', null);

  return out;
}


// ─────────────────────────────────────────────────────────────────
// Posting payload builder — produces the body for
// POST /api/erp/purchase-accounting/postings/from-bill/:purchaseBillId.
//
// The backend accepts an OPTIONAL body — { postingDate, narration,
// remarks, forceRepost, itcStatus, itcClaimedPeriod } — and ignores
// keys it doesn't recognise. We send a stable shape so the API logs
// are easy to read; nullable / blank keys are stripped so an "empty"
// POST renders as `{}`, not as a noisy payload full of nulls.
// ─────────────────────────────────────────────────────────────────

export function buildPurchaseAccountingPostPayload(opts) {
  const o = opts && typeof opts === 'object' ? opts : {};
  const out = {};

  const postingDate = pick(o, 'posting_date', 'postingDate', null);
  if (postingDate !== null && postingDate !== undefined) {
    if (postingDate instanceof Date && !Number.isNaN(postingDate.getTime())) {
      out.postingDate = postingDate.toISOString().slice(0, 10);
    } else {
      const s = String(postingDate).trim();
      if (s) out.postingDate = s;
    }
  }

  const narration = pick(o, 'narration', 'narration', null);
  if (narration !== null && narration !== undefined) {
    const s = String(narration).trim();
    if (s) out.narration = s;
  }

  const remarks = pick(o, 'remarks', 'remarks', null);
  if (remarks !== null && remarks !== undefined) {
    const s = String(remarks).trim();
    if (s) out.remarks = s;
  }

  // Boolean force-repost: send only when explicitly true so the API
  // logs a meaningful payload.
  const forceRepost = pick(o, 'force_repost', 'forceRepost', undefined);
  if (forceRepost === true) {
    out.forceRepost = true;
  }

  const itcStatusRaw = pickStringUpper(o, 'itc_status', 'itcStatus');
  if (itcStatusRaw && ITC_STATUSES_SET.has(itcStatusRaw)) {
    out.itcStatus = itcStatusRaw;
  }

  const itcClaimedPeriod = pick(o, 'itc_claimed_period', 'itcClaimedPeriod', null);
  if (itcClaimedPeriod !== null && itcClaimedPeriod !== undefined) {
    const s = String(itcClaimedPeriod).trim();
    if (s) out.itcClaimedPeriod = s;
  }

  return out;
}


// ─────────────────────────────────────────────────────────────────
// Debit / credit math — the preview grid renders the per-line
// debit + credit columns and a footer row with totals. The backend
// builds the same totals server-side; this helper exists so the
// footer stays in sync as the operator scrolls.
//
// Tolerant: ignores non-array input, ignores non-object rows, never
// throws. Totals are rounded half-up to 2dp (rupee precision).
// ─────────────────────────────────────────────────────────────────

export function calculateDebitCreditTotals(lines) {
  let totalDebit  = 0;
  let totalCredit = 0;

  if (Array.isArray(lines)) {
    for (const line of lines) {
      if (!line || typeof line !== 'object') continue;
      totalDebit  += toNumber(pick(line, 'debit_amount',  'debitAmount',  0));
      totalCredit += toNumber(pick(line, 'credit_amount', 'creditAmount', 0));
    }
  }

  const d = round2(totalDebit);
  const c = round2(totalCredit);
  return {
    totalDebit:  d,
    totalCredit: c,
    difference:  round2(d - c)
  };
}


// ─────────────────────────────────────────────────────────────────
// Preview balance check — a voucher is balanced when |Σdebit −
// Σcredit| ≤ eps. The backend uses the same epsilon (BALANCE_EPSILON
// = 0.01) so the UI agrees with the server on the boundary.
// ─────────────────────────────────────────────────────────────────

export function validateAccountingPreviewBalance(lines, eps) {
  const tolerance = typeof eps === 'number' && Number.isFinite(eps) && eps >= 0
    ? eps
    : 0.01;
  const { difference } = calculateDebitCreditTotals(lines);
  return Math.abs(difference) <= tolerance;
}


// ─────────────────────────────────────────────────────────────────
// Posting-status labels + tag colours. Colours align with the AntD
// Tag palette guidance in docs/23-antd-component-standards.md so the
// posting grids match the rest of the control-plane chrome.
// ─────────────────────────────────────────────────────────────────

const POSTING_STATUS_LABELS = {
  NOT_POSTED:     'Not Posted',
  POSTING_READY:  'Ready to Post',
  POSTED:         'Posted',
  POSTING_FAILED: 'Posting Failed',
  CANCELLED:      'Cancelled'
};

const POSTING_STATUS_COLORS = {
  NOT_POSTED:     'default',
  POSTING_READY:  'processing',
  POSTED:         'success',
  POSTING_FAILED: 'error',
  CANCELLED:      'default'
};

export function accountingStatusLabel(status) {
  if (!status) return '';
  const key = String(status).trim().toUpperCase();
  return POSTING_STATUS_LABELS[key] || key;
}

export function accountingStatusColor(status) {
  if (!status) return 'default';
  const key = String(status).trim().toUpperCase();
  return POSTING_STATUS_COLORS[key] || 'default';
}


// ─────────────────────────────────────────────────────────────────
// ITC-status labels + tag colours.
// ─────────────────────────────────────────────────────────────────

const ITC_STATUS_LABELS = {
  ELIGIBLE:       'Eligible',
  BLOCKED:        'Blocked',
  REVERSED:       'Reversed',
  CLAIMED_LATER:  'Claimed Later'
};

const ITC_STATUS_COLORS = {
  ELIGIBLE:       'success',
  BLOCKED:        'error',
  REVERSED:       'warning',
  CLAIMED_LATER:  'processing'
};

export function itcStatusLabel(status) {
  if (!status) return '';
  const key = String(status).trim().toUpperCase();
  return ITC_STATUS_LABELS[key] || key;
}

export function itcStatusColor(status) {
  if (!status) return 'default';
  const key = String(status).trim().toUpperCase();
  return ITC_STATUS_COLORS[key] || 'default';
}


// ─────────────────────────────────────────────────────────────────
// Capability gate — drives the "Post to Accounts" CTA on the bill
// detail / preview page. The backend remains the authority; this
// guard hides the CTA for operators who clearly can't succeed:
//
//   - bill must be APPROVED / POSTED / MATCHED (i.e. finalised on the
//     purchase side; draft / cancelled bills are blocked)
//   - bill match status must be MATCHED or NOT_REQUIRED (a PENDING
//     or REJECTED match means the bill is still under reconciliation)
//   - existing posting (if any) must NOT be POSTED — re-posting is
//     blocked from the UI; the backend supports forceRepost for the
//     admin path but we don't expose it through the default gate
//   - NOT_POSTED / POSTING_READY / POSTING_FAILED / CANCELLED are all
//     OK to re-attempt (FAILED + CANCELLED can be retried; the backend
//     decides if it's allowed)
// ─────────────────────────────────────────────────────────────────

export function canPostPurchaseBillToAccounts(opts) {
  const o = opts && typeof opts === 'object' ? opts : {};

  const billStatus = pickStringUpper(o, 'bill_status', 'billStatus');
  if (
    billStatus !== BILL_STATUS_APPROVED &&
    billStatus !== BILL_STATUS_POSTED   &&
    billStatus !== BILL_STATUS_MATCHED
  ) {
    return false;
  }

  const billMatchStatus = pickStringUpper(o, 'bill_match_status', 'billMatchStatus');
  // Empty string allowed → tenants that don't run 3-way match still
  // post bills. Only reject when an explicit non-OK state is reported.
  if (billMatchStatus) {
    if (
      billMatchStatus !== BILL_MATCH_OK &&
      billMatchStatus !== BILL_MATCH_NOT_REQUIRED
    ) {
      // Anything else (PENDING / REJECTED / unknown) blocks the CTA.
      void BILL_MATCH_PENDING;
      void BILL_MATCH_REJECTED;
      return false;
    }
  }

  const existing = pickStringUpper(o, 'existing_posting_status', 'existingPostingStatus');
  if (existing === 'POSTED') return false;
  // POSTING_READY blocks: there's a draft awaiting post — the UI
  // should route the user to "Post existing draft" not "Create new".
  if (existing === 'POSTING_READY') return false;

  return true;
}


// ─────────────────────────────────────────────────────────────────
// Cancel-gate — drives the "Cancel Posting" CTA. Only a POSTED
// voucher can be cancelled (the backend reverses ledger impact + sets
// cancelled_at). FAILED / DRAFT postings are removed, not cancelled.
// ─────────────────────────────────────────────────────────────────

export function canCancelPurchaseAccountingPosting(postingStatus) {
  if (!postingStatus) return false;
  const key = String(postingStatus).trim().toUpperCase();
  return key === 'POSTED';
}


// ─────────────────────────────────────────────────────────────────
// summarizePostingPreview — single-glance summary for the preview
// card: total debit, total credit, difference, balanced flag, blocker
// count + line count.
//
// "Blockers" are rows that the backend has tagged with a non-null
// `blockerReason` (e.g. missing ledger code, unresolved item type).
// The UI shows the count in the summary card and the full list in a
// drawer.
//
// Tolerant: ignores non-object input, ignores non-array `lines`,
// reuses calculateDebitCreditTotals so the totals match the footer.
// ─────────────────────────────────────────────────────────────────

export function summarizePostingPreview(preview) {
  const out = {
    totalDebit:   0,
    totalCredit:  0,
    difference:   0,
    balanced:     true,
    blockerCount: 0,
    lineCount:    0
  };

  if (!preview || typeof preview !== 'object') return out;

  const lines = Array.isArray(preview.lines) ? preview.lines : [];
  const totals = calculateDebitCreditTotals(lines);
  out.totalDebit  = totals.totalDebit;
  out.totalCredit = totals.totalCredit;
  out.difference  = totals.difference;
  out.balanced    = Math.abs(totals.difference) <= 0.01;
  out.lineCount   = lines.filter((l) => l && typeof l === 'object').length;

  // Server-supplied blocker list wins; otherwise count lines that
  // carry a non-empty blockerReason.
  if (Array.isArray(preview.blockers)) {
    out.blockerCount = preview.blockers.filter(
      (b) => b && (typeof b === 'string' ? b.trim() : typeof b === 'object')
    ).length;
  } else {
    for (const l of lines) {
      if (!l || typeof l !== 'object') continue;
      const reason = pick(l, 'blocker_reason', 'blockerReason', null);
      if (reason !== null && reason !== undefined && String(reason).trim() !== '') {
        out.blockerCount += 1;
      }
    }
  }

  return out;
}


// ─────────────────────────────────────────────────────────────────
// Sanity references — keep the importer pattern at the top of this
// module honest. Touching these here keeps a future refactor from
// silently dropping an export.
// ─────────────────────────────────────────────────────────────────
void POSTING_STATUSES_SET;
void POSTING_LINE_TYPES_SET;
void EVENT_TYPES_SET;
